/**
 * Vue Router Configuration
 * 
 * Defines routes and navigation guards for authentication.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { isLocalAuthBypassEnabled } from '@/config/authConfig'
import { Roles } from '@/types'

// Route definitions
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false, title: 'Sign In' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { requiresAuth: true, title: 'Complete Registration' },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, requiresRegistration: true, title: 'Dashboard' },
  },
  {
    path: '/devices',
    name: 'devices',
    component: () => import('@/views/DevicesView.vue'),
    meta: { 
      requiresAuth: true, 
      requiresRegistration: true,
      title: 'Devices',
      roles: [Roles.SUPER_ADMIN, Roles.DEVICE_ADMIN, Roles.DEVICE_USER],
    },
  },
  {
    path: '/claim',
    redirect: '/pending-claims',
  },
  {
    path: '/pending-claims',
    name: 'pending-claims',
    component: () => import('@/views/PendingClaimsView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'Pending Claims',
      roles: [Roles.SUPER_ADMIN, Roles.DEVICE_ADMIN],
    },
  },
  {
    path: '/files',
    name: 'files',
    component: () => import('@/views/FilesView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'SOR Files',
      roles: [Roles.SUPER_ADMIN, Roles.SOR_ANALYST],
    },
  },
  {
    path: '/config',
    redirect: '/sequence',
  },
  {
    path: '/sequence',
    name: 'sequence',
    component: () => import('@/views/SequenceView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'Sequence',
      roles: [Roles.SUPER_ADMIN, Roles.SOR_ANALYST],
    },
  },
  {
    path: '/sequence/edit',
    name: 'sequence-edit',
    component: () => import('@/views/SequenceEditorView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'Sequence Editor',
      roles: [Roles.SUPER_ADMIN, Roles.SOR_ANALYST],
    },
  },
  {
    path: '/analysis',
    redirect: '/analysis/loadtraces',
  },
  {
    path: '/analysis/loadtraces',
    name: 'Analysis',
    component: () => import('@/views/AnalysisView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'Load Trace',
      roles: [Roles.SUPER_ADMIN, Roles.SOR_ANALYST],
    },
  },
  {
    path: '/analysis/bidi',
    name: 'AnalysisBiDi',
    component: () => import('@/views/AnalysisBiDiView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'Bi-di Analysis',
      roles: [Roles.SUPER_ADMIN, Roles.SOR_ANALYST],
    },
  },
  {
    path: '/analysis/comparison',
    name: 'AnalysisComparison',
    component: () => import('@/views/AnalysisComparisonView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'Trace Comparison',
      roles: [Roles.SUPER_ADMIN, Roles.SOR_ANALYST],
    },
  },
  {
    path: '/admin/tenants',
    name: 'tenant-overview',
    component: () => import('@/views/TenantOverviewView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'Tenant Overview',
      roles: [Roles.SUPER_ADMIN],
    },
  },
  {
    path: '/certificates',
    name: 'certificates',
    component: () => import('@/views/CertificatesView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'CA Certificates',
      roles: [Roles.SUPER_ADMIN],
    },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/UsersView.vue'),
    meta: {
      requiresAuth: true,
      requiresRegistration: true,
      title: 'User Management',
      roles: [Roles.SUPER_ADMIN, Roles.CUSTOMER_ADMIN],
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true, requiresRegistration: true, title: 'Profile' },
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/HelpView.vue'),
    meta: { requiresAuth: true, requiresRegistration: true, title: 'User Guide' },
  },
  {
    path: '/unauthorized',
    name: 'unauthorized',
    component: () => import('@/views/UnauthorizedView.vue'),
    meta: { requiresAuth: false, title: 'Access Denied' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { requiresAuth: false, title: 'Page Not Found' },
  },
]

// Create router instance
const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const localAuthBypass = isLocalAuthBypassEnabled()

  // Ensure auth is initialized
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  // Update page title
  document.title = to.meta.title 
    ? `${to.meta.title} | OTDR Management`
    : 'OTDR Device Management'

  // Check if route requires authentication
  if (!localAuthBypass && to.meta.requiresAuth !== false) {
    if (!authStore.isAuthenticated) {
      // Redirect to login
      return next({ name: 'login', query: { redirect: to.fullPath } })
    }

    // Check if registration is complete
    if (to.meta.requiresRegistration && !authStore.isRegistrationComplete) {
      if (to.name !== 'register') {
        return next({ name: 'register' })
      }
    }

    // If user is on register page but already registered, redirect to dashboard
    if (to.name === 'register' && authStore.isRegistrationComplete) {
      return next({ name: 'dashboard' })
    }

    // Check role requirements
    const requiredRoles = to.meta.roles as string[] | undefined
    if (requiredRoles && requiredRoles.length > 0) {
      if (!authStore.hasAnyRole(requiredRoles)) {
        return next({ name: 'unauthorized' })
      }
    }
  }

  // If SuperAdmin navigating to '/', redirect to tenant overview
  if (to.path === '/' && authStore.isSuperAdmin) {
    return next('/admin/tenants')
  }

  // If authenticated user tries to access login, redirect to dashboard
  if (to.name === 'login' && (authStore.isAuthenticated || localAuthBypass)) {
    if (!authStore.isRegistrationComplete) {
      return next({ name: 'register' })
    }
    return next({ name: 'dashboard' })
  }

  // Local-only flow: go directly to analysis view.
  if (localAuthBypass && to.name === 'dashboard') {
    return next({ name: 'Analysis' })
  }

  next()
})

export default router

// Type augmentation for route meta
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresRegistration?: boolean
    title?: string
    roles?: string[]
  }
}
